import React from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import { Form } from "react-bootstrap"
import { toast, ToastContainer } from "react-toastify"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import SecondaryButton from "../../components/subComponents/SecondaryButton"
import { request } from "../../services/request"

const ChangePassword: React.FC = () => {
  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Please enter your current password"),
    newPassword: Yup.string()
      .required("Please enter a new password")
      .min(6, "New password must be at least 6 characters"),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Please enter confirm new password"),
  })

  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false)
  const [showNewPassword, setShowNewPassword] = React.useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = React.useState(false)

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await request({
          url: `/admin/changePassword`,
          method: "POST",
          data: {
            password: values.currentPassword,
            new_password: values.newPassword,
          },
        })
        if (response.success) {
          toast.success(response.message || "Password changed successfully")
          formik.resetForm()
        } else {
          toast.error(response.message || "Failed to change password")
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong")
      }
    },
  })

  return (
    <section className='dashboard-section'>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />

      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div className='dashboard-table-title-section title-line'>
              <h2>Change Password</h2>
            </div>
          </div>
          <div className='col-xl-5 col-lg-10 col-md-10 col-12 mt-4'>
            <Form onSubmit={formik.handleSubmit} className='customer-edit-form'>
              <div className='login-input-section input-section mb-3'>
                <div>
                  <label htmlFor='newPassword'>Current Password</label>
                </div>
                <Form.Control
                  type={showCurrentPassword ? "text" : "password"}
                  id='currentPassword'
                  name='currentPassword'
                  placeholder='Enter current password'
                  value={formik.values.currentPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.currentPassword && formik.touched.currentPassword}
                />
                <span className='text-red-500 text-xs'>
                  {formik.touched.currentPassword && formik.errors.currentPassword}
                </span>
                {formik.values.currentPassword.length > 0 && (
                  <button
                    type='button'
                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                    // className='absolute right-2.5 top-11 cursor-pointer showPassIcon'
                    className={`absolute top-11 cursor-pointer showResetPassIcon ${!!formik.errors.currentPassword && formik.touched.currentPassword && "right-22"}`}
                  >
                    {showCurrentPassword ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                        className='passIcon'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z'
                        />
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                        className='passIcon'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88'
                        />
                      </svg>
                    )}
                  </button>
                )}
              </div>

              <div className='login-input-section input-section mb-3'>
                <div>
                  <label htmlFor='newPassword'>New Password</label>
                </div>
                <Form.Control
                  type={showNewPassword ? "text" : "password"}
                  id='newPassword'
                  name='newPassword'
                  placeholder='Enter new password'
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.newPassword && formik.touched.newPassword}
                />
                <span className='text-red-500 text-xs'>
                  {formik.touched.newPassword && formik.errors.newPassword}
                </span>
                {formik.values.newPassword.length > 0 && (
                  <button
                    type='button'
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    className={`absolute top-11 cursor-pointer showResetPassIcon ${!!formik.errors.newPassword && formik.touched.newPassword && "right-22"}`}
                  >
                    {showNewPassword ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                        className='passIcon'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z'
                        />
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                        className='passIcon'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88'
                        />
                      </svg>
                    )}
                  </button>
                )}
              </div>

              <div className='login-input-section input-section mb-3'>
                <div className=''>
                  <label htmlFor='confirmNewPassword'>Confirm New Password</label>
                </div>
                <Form.Control
                  type={showConfirmNewPassword ? "text" : "password"}
                  id='confirmNewPassword'
                  name='confirmNewPassword'
                  placeholder='Confirm new password'
                  value={formik.values.confirmNewPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.confirmNewPassword && formik.touched.confirmNewPassword
                  }
                />
                <span className='text-red-500 text-xs'>
                  {formik.touched.confirmNewPassword && formik.errors.confirmNewPassword}
                </span>
                {formik.values.confirmNewPassword.length > 0 && (
                  <button
                    type='button'
                    onClick={() => setShowConfirmNewPassword((prev) => !prev)}
                    className={`absolute top-11 cursor-pointer showResetPassIcon ${!!formik.errors.confirmNewPassword && formik.touched.confirmNewPassword && "right-22"}`}
                  >
                    {showConfirmNewPassword ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                        className='passIcon'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z'
                        />
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                        className='passIcon'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88'
                        />
                      </svg>
                    )}
                  </button>
                )}
              </div>

              <div className='d-flex gap-3 mt-4'>
                <div className='d-flex gap-3'>
                  <PrimaryButton type='submit' label='Update' className='editFormButton px-4' />
                  <SecondaryButton
                    type='button'
                    onClick={() => formik.resetForm()}
                    label='Reset'
                    className='editFormButton px-4'
                  />
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ChangePassword
